import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits.
   *
   * "−" is never disabled here. It used to be disabled at the minimum, which is
   * the state of most cart lines (quantity 1) — leaving a 44x44px control that
   * looked live, kept `cursor: pointer`, and swallowed the tap. Shoppers press it
   * expecting the line to go away. Now it does exactly that, so the control
   * always has an outcome. Note this also removes a server/client mismatch: the
   * server renders "−" enabled and the old code disabled it on hydration.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = false;
    this.#setMinusRemovesLine(value <= min);

    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Stepping below the minimum removes the line instead of clamping to it.
   *
   * The base class clamps with `Math.max(min, ...)` and still dispatches the
   * unchanged quantity, so the request was a no-op the shopper could not see.
   *
   * @param {number} stepMultiplier - Positive for increase, negative for decrease.
   */
  updateQuantity(stepMultiplier) {
    const { min, value } = this.getCurrentValues();

    if (stepMultiplier < 0 && value <= min) {
      const { quantityInput } = this.refs;

      this.dispatchEvent(new QuantitySelectorUpdateEvent(0, Number(quantityInput.dataset.cartLine) || undefined));
      return;
    }

    super.updateQuantity(stepMultiplier);
  }

  /**
   * Keeps the accessible name honest when "−" will remove the line rather than
   * decrement it. The label is borrowed from the row's own remove button so the
   * string stays localised rather than hardcoded here.
   *
   * @param {boolean} removesLine - Whether pressing "−" now removes the line.
   */
  #setMinusRemovesLine(removesLine) {
    const { minusButton } = this.refs;

    minusButton.toggleAttribute('data-removes-line', removesLine);

    const removeLabel = this.closest('tr')?.querySelector('.cart-items__remove')?.getAttribute('aria-label');

    if (removesLine && removeLabel) minusButton.setAttribute('aria-label', removeLabel);
    else minusButton.removeAttribute('aria-label');
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
